import printSplJob from '@afosto/utils-react/spooler/printSplJob';
import splHelpers from '@afosto/utils-react/spooler/splHelpers';
import getFullName from '@afosto/utils/getFullName';
import arrayFromNumber from '@afosto/utils/array/arrayFromNumber';
import fetchShipmentAddressing from '../fetchShipmentAddressing';
import fetchShippingMethod from '../fetchShippingMethod';
import getShippingMethodName from '../getShippingMethodName';
import { datetime as datetimeFormatter } from '@afosto/app-components-react/DataGrid/formatters';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const PROPERTY_OPTIONS = { size: 30, width: 14 };

const getAddressLines = address => {
  const {
    organisation,
    address_line_1: addressLine1,
    address_line_2: addressLine2,
    postal_code: postalCode,
    locality,
    country_code: countryCode,
  } = address;

  return [
    ...(organisation ? [splHelpers.text(organisation, PROPERTY_OPTIONS)] : []),
    splHelpers.text(getFullName(address), PROPERTY_OPTIONS),
    ...(addressLine1 ? [splHelpers.text(addressLine1, PROPERTY_OPTIONS)] : []),
    ...(addressLine2 ? [splHelpers.text(addressLine2, PROPERTY_OPTIONS)] : []),
    ...(postalCode || locality || countryCode
      ? [
          splHelpers.text(
            `${postalCode} ${locality}${countryCode && `, ${countryCode}`}`,
            PROPERTY_OPTIONS,
          ),
        ]
      : []),
  ];
};

const printParcelTicket = async (intl, parcel, options = {}) => {
  try {
    const shipmentsResponse = await shipmentsApi.viewShipment(parcel?.shipment_id).catch(() => {});
    const { data: shipment = {} } = shipmentsResponse;

    const addressing = await fetchShipmentAddressing(shipment, ['from']).catch(() => {});
    const shippingMethod = await fetchShippingMethod(shipment?.method_id).catch(() => null);

    return printSplJob(
      [
        ...arrayFromNumber(2).map(() => splHelpers.feed()),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.shippingNumber)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(shipment?.number ?? '-', PROPERTY_OPTIONS),
        ]),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.shippingMethod)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(getShippingMethodName(intl, shippingMethod) ?? '-', PROPERTY_OPTIONS),
        ]),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.expectedAt)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(
            shipment?.expected_at ? datetimeFormatter(shipment?.expected_at) : '-',
            PROPERTY_OPTIONS,
          ),
        ]),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.shipAt)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(
            parcel?.ship_at ? datetimeFormatter(parcel?.ship_at) : '-',
            PROPERTY_OPTIONS,
          ),
        ]),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.parcelCount)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(shipment?.parcels?.length ?? '-', PROPERTY_OPTIONS),
        ]),
        ...arrayFromNumber(2).map(() => splHelpers.feed()),
        splHelpers.text(`${intl.formatMessage(translations.properties.toAddress)}:`, {
          ...PROPERTY_OPTIONS,
          style: 'bold',
        }),
        ...arrayFromNumber(2).map(() => splHelpers.feed()),
        ...(addressing?.to ? getAddressLines(addressing?.to) : []),
        ...arrayFromNumber(2).map(() => splHelpers.feed()),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.parcelNumber)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(parcel?.number ?? '-', PROPERTY_OPTIONS),
        ]),
        splHelpers.table([
          splHelpers.text(
            `${intl.formatMessage(translations.properties.itemCount)}:`,
            PROPERTY_OPTIONS,
          ),
          splHelpers.text(parcel?.count ?? '-', PROPERTY_OPTIONS),
        ]),
        ...arrayFromNumber(4).map(() => splHelpers.feed()),
        splHelpers.barcode(parcel?.number, { align: 'center', height: 100 }),
        splHelpers.cut(),
      ],
      `Print parcel barcode for parcel ${parcel?.id}`,
      options,
    );
  } catch (err) {
    return Promise.reject(err);
  }
};

export default printParcelTicket;
